import { omit } from 'ramda'
import { array, boolean, mixed, number, object, string } from 'yup'

import { IAssets,IAssetTypes } from 'types/assets'
import { IComparison } from 'types/comparison'
import { emptyComparison, initialComparedProduct } from 'types/lib/comparison'
import {
  getDefaultComparisonCustom,
  IComparisonCustom,
  isComparisonCustomEmpty,
  normalizeComparisonCustom,
  stripComparisonCustomRowImagesForUpload,
  stripEmptyComparisonCustomRows,
  stripTrailingEmptyComparisonCustomColumns
} from 'types/lib/comparisonCustom'
import { isVideoSizeAllowed, isVideoTypeAllowed } from 'types/lib/files'
import {
  initialVideoFunction,
  VIDEO_DESCRIPTIONS_TYPES} from 'types/lib/videos'
import { IEditProduct, IHotpoint } from 'types/products'
import { ISummaryProperties } from 'types/summary'
import { VideoDescriptionItem, VideoFunctionItem } from 'types/videos'
import {
  buildAssetSlotsFromProductAssetRows,
  extractAssetSlotReferenceId,
  getGroupedAssetArrayForFormField
} from 'utils/assetsUtils'
import { isHaierBrand, isHaierHooverCandy, isSchneider } from 'utils/brandUtils'
import { normalizeImageAiUsageForForm } from 'utils/imageAiUsage'
import { prepareAddProductFormValues } from 'utils/strings'
import {
  getMinAssetSlotCountForCardTemplate,
  resolveCardTemplate
} from 'utils/templateUtils'

export const MAIN_VIDEO_DESCRIPTIONS = {
  DEFAULT: 'DEFAULT',
  euro: 'Euro',
  mediaExpert: 'MediaExpert'
}

// @sieradz - communication with API passed out to [{id: ''}]
// const STRING = ''
const OBJECT_WITH_ID = { id: '' }

// @sieradz - communication with API passed out to [{id: ''}]
// const STRINGS = ['banner', 'first_lead', 'second_lead']
// const STRINGS = ['']
const ARRAYS_OF_OBJECT_WITH_ID = {
  banner: 1,
  first_lead: 1,
  features: 4,
  intro: 1,
  second_lead: 1,
  attributes: 4,
  icons: 8,
  functions: 32,
  icon_functions: 4,
  photos: 7,
  slider: 9,
  accessories: 10
}

/**
 * Product POST/PATCH asset reference fields (multipart `FormData`, not JSON body).
 *
 * - Create: `POST /products/form`
 * - Update: `PATCH /products/:id/form`
 * - Upload images as Photos first (`POST /assets/form`), then reference UUIDs here.
 * - Field name must match category asset type (e.g. `Slider`, `Photos`).
 * - Value: `JSON.stringify([{ id: "<uuid>" }, { id: "" }, ...])` — IDs only, not GET objects.
 * - PATCH: omit `Slider` to leave the product slider list unchanged.
 */
/** React-hook-form key; FormData key is {@link SLIDER_API_FIELD_NAME}. */
export const SLIDER_FORM_FIELD_KEY = 'slider'

/** API / DB asset type name (FormData part name). */
export const SLIDER_API_FIELD_NAME = 'Slider'

/** RHF key; FormData part name (intentional API spelling). */
export const DIMENSIONS_ACCESSORIES_FORM_FIELD_KEY = 'dimensionsAccesories'

export const DIMENSIONS_ACCESSORIES_API_FIELD_NAME = 'dimensionsAccesories'

/** Same capacity as product Icons section ({@link ARRAYS_OF_OBJECT_WITH_ID.icons}). */
export const DIMENSIONS_ACCESSORIES_DEFAULT_SLOT_COUNT =
  ARRAYS_OF_OBJECT_WITH_ID.icons

/** Strip null/undefined and accidental `"undefined"` / `"null"` strings from API → form. */
export function normalizeOptionalProductText(value: unknown): string {
  if (value === undefined || value === null) {
    return ''
  }

  const text = String(value).trim()

  if (text === 'undefined' || text === 'null') {
    return ''
  }

  return text
}

/** Map category asset type name → product form field key (e.g. "Intro Section" → intro, "Slider" → slider). */
export function assetTypeNameToFormKey(name: string): string {
  const slug = name.replace(/\s/g, '_').toLowerCase()

  if (slug === 'intro_section' || slug === 'introsection') return 'intro'
  if (slug === 'slider') return SLIDER_FORM_FIELD_KEY
  if (slug === 'photos' || slug.startsWith('photos')) return 'photos'

  return slug
}

export const ORDER_OF_ASSETS = [
  // @sieradz - used to detect assets(asets are separate creations and are selected in the form, separate section + capital letter for payload)
  'banner',
  'features',
  'intro',
  'first_lead',
  'attributes',
  'icons',
  'second_lead',
  'functions',
  'icon_functions',
  'photos',
  'slider',
  'accessories'
]

export const FORM_SECTIONS = {
  general_info: { id: 'general_info', isAsset: false },
  banner: { id: 'banner', isAsset: true },
  first_lead: { id: 'first_lead', isAsset: true },
  features: { id: 'features', isAsset: true },
  intro: { id: 'intro', isAsset: true },
  icons: { id: 'icons', isAsset: true },
  hotpoints: { id: 'hotpoints', isAsset: false },
  video_functions: { id: 'video_functions', isAsset: false },
  second_lead: { id: 'second_lead', isAsset: true },
  functions: { id: 'functions', isAsset: true },
  dimensions: { id: 'dimensions', isAsset: false },
  summary: { id: 'summary', isAsset: false },
  comparison: { id: 'comparison', isAsset: false },
  photos: { id: 'photos', isAsset: true },
  slider: { id: 'slider', isAsset: true },
  accessories: { id: 'accessories', isAsset: true }
}

export interface IIdString {
  id: string
}
export interface ProductFormValues {
  sku: string
  skuCodes?: string
  name: string
  shortName: string
  title?: string
  subTitle?: string
  packshotUrl?: string
  packshot: FileList | null
  packshotAlt: string
  packshotAiUsage?: 'generated' | 'edited' | null
  packshotBackgroundDisabled?: boolean
  packshotPreviewUrl?: string
  openedPackshotUrl?: string
  openedPackshot?: FileList | null
  openedPackshotAlt?: string
  openedPackshotAiUsage?: 'generated' | 'edited' | null
  country: string
  productCategoryId: string
  banner: IIdString[]
  first_lead?: IIdString[]
  attributes?: IIdString[]
  icons?: IIdString[]
  features?: IIdString[]
  intro?: IIdString[]
  hotpoints?: FileList | null
  hotpointsAlt?: string
  hotpointsAiUsage?: 'generated' | 'edited' | null
  hotpointsMobile?: FileList | null
  hotpointsMobileAlt?: string
  hotpointsMobileAiUsage?: 'generated' | 'edited' | null
  hotpointsUrl?: string
  hotpointsMobileUrl?: string
  hotpointsProperties?: IHotpoint[] | null
  hotpointsMobileProperties?: IHotpoint[] | null
  second_lead?: IIdString[]
  mainVideoTitle: string
  mainVideoYoutubeId?: string
  mainVideo?: FileList | null
  mainVideoDescriptions?: VideoDescriptionItem[]
  functions?: IIdString[]
  dimensionsDescription?: string
  dimensions?: FileList | null
  dimensionsUrl?: string
  dimensionsAlt?: string
  dimensionsTitle?: string
  dimensionsTechnicalDrawing?: FileList | null
  dimensionsTechnicalDrawingUrl?: string
  dimensionsTechnicalDrawingAlt?: string
  dimensionsAccesories?: IIdString[]
  summary?: FileList | null
  summaryUrl?: string
  summaryProperties?: ISummaryProperties[]
  summaryDescription?: string
  summaryAlt?: string
  summaryAiUsage?: 'generated' | 'edited' | null
  videoFunctions?: VideoFunctionItem[]
  icon_functions?: IIdString[]
  comparison?: IComparison
  /** Schneider: comparison table without linked products */
  comparisonCustom?: IComparisonCustom | null
  /** Per-row new image files (0-based indices); same length as comparisonCustom.rows */
  comparisonCustomRowImageFiles?: (File | null)[] | null
  photos?: IIdString[]
  slider?: IIdString[]
  accessories?: IIdString[]
  bottomVideoYoutubeId?: string
  bottomVideo?: FileList | null
  leavePackshot?: boolean
  leaveMainVideo?: boolean
  leaveSourceGraphicFile?: boolean
  /** Internal-only (Haier / Hoover / Candy); optional string */
  internalNote?: string
  /** Functions layout template (Haier / Candy). Optional — omitted when unset. */
  cardTemplate?: string | null
  /** Hotpoints layout template (Haier). Optional — omitted when unset. */
  hotpointTemplate?: string | null
}

interface AddProductFormFieldNames {
  sku: 'sku'
  skuCodes: 'skuCodes'
  name: 'name'
  shortName: 'shortName'
  title: 'title',
  subTitle: 'subTitle',
  packshotUrl: 'packshotUrl'
  packshotAlt: 'packshotAlt'
  packshotAiUsage: 'packshotAiUsage'
  packshotBackgroundDisabled: 'packshotBackgroundDisabled'
  openedPackshotUrl: 'openedPackshotUrl'
  openPackshotAlt: 'openedPackshotAlt'
  openedPackshotAiUsage: 'openedPackshotAiUsage'
  packshot: 'packshot'
  openedPackshot: 'openedPackshot'
  packshotPreviewUrl?: 'packshotPreviewUrl'
  country: 'country'
  productCategoryId: 'productCategoryId'
  banner: 'banner'
  first_lead?: 'first_lead'
  attributes?: 'attributes'
  icons?: 'icons'
  features?: 'features'
  intro?: 'intro'
  hotpoints: 'hotpoints'
  hotpointsAlt: 'hotpointsAlt'
  hotpointsAiUsage: 'hotpointsAiUsage'
  hotpointsMobile: 'hotpointsMobile'
  hotpointsMobileAlt: 'hotpointsMobileAlt'
  hotpointsMobileAiUsage: 'hotpointsMobileAiUsage'
  hotpointsProperties: 'hotpointsProperties'
  hotpointsMobileProperties: 'hotpointsMobileProperties'
  second_lead?: 'second_lead'
  mainVideoTitle: 'mainVideoTitle'
  mainVideoYoutubeId: 'mainVideoYoutubeId'
  mainVideo: 'mainVideo'
  mainVideoDescriptions?: 'mainVideoDescriptions'
  functions?: 'functions'
  dimensionsDescription?: 'dimensionsDescription'
  dimensions?: 'dimensions'
  dimensionsUrl?: 'dimensionsUrl'
  dimensionsAlt?: 'dimensionsAlt'
  dimensionsTitle?: 'dimensionsTitle'
  dimensionsTechnicalDrawing?: 'dimensionsTechnicalDrawing'
  dimensionsTechnicalDrawingUrl?: 'dimensionsTechnicalDrawingUrl'
  dimensionsTechnicalDrawingAlt?: 'dimensionsTechnicalDrawingAlt'
  dimensionsAccesories?: 'dimensionsAccesories'
  comparison?: 'comparison'
  comparisonCustom?: 'comparisonCustom'
  summary?: 'summary'
  summaryUrl?: 'summaryUrl'
  summaryAiUsage?: 'summaryAiUsage'
  summaryProperties?: 'summaryProperties'
  summaryDescription?: 'summaryDescription'
  summaryAlt?: 'summaryAlt'
  videoFunctions?: 'videoFunctions'
  icon_functions?: 'icon_functions'
  photos?: 'photos'
  slider?: 'slider'
  accessories?: 'accessories'
  bottomVideoYoutubeId?: 'bottomVideoYoutubeId'
  bottomVideo?: 'bottomVideo'
  leavePackshot?: 'leavePackshot'
  leaveMainVideo?: 'leaveMainVideo'
  leaveSourceGraphicFile?: 'leaveSourceGraphicFile'
  internalNote?: 'internalNote'
  cardTemplate?: 'cardTemplate'
  hotpointTemplate?: 'hotpointTemplate'
}

export interface IDescriptions {
  partnerName: string
  description: string
}
export interface IVideoFunctions {
  youtubeId: string
  descriptions: IDescriptions[]
}

export interface IInitialAssets {
  [k: string]: {
    id: string
  }[]
}

export interface MapProductFormPayloadOptions {
  isUpdate?: boolean
  /** When updating, only append *AiUsage fields the user changed (or file slots — see caller). */
  dirtyFields?: Partial<Record<string, boolean | object>>
}

/** Read `assets.Slider` from GET product (API key) regardless of casing. */
export function getSliderSlotsFromProductAssets(
  assets: Record<string, unknown> | undefined | null
): IIdString[] {
  if (!assets) {
    return []
  }

  const raw =
    assets[SLIDER_API_FIELD_NAME] ??
    assets[SLIDER_FORM_FIELD_KEY] ??
    assets.Slider

  return serializeProductAssetSlotArray(raw)
}

/** Strip GET asset objects to `{ id }` slots for POST/PATCH (never send imageUrl, etc.). */
export function serializeProductAssetSlotArray(value: unknown): IIdString[] {
  if (!Array.isArray(value)) {
    return []
  }

  return value.map(item => ({ id: extractAssetSlotReferenceId(item) }))
}

/** Resolve edit-form asset slots from grouped `assets.*` and positioned `productAssets[]`. */
export function resolveProductFormAssetSlots(
  product: Record<string, unknown> | null | undefined,
  formFieldKey: string,
  slotCount: number
): IIdString[] {
  const assets = product?.assets as Record<string, unknown> | undefined
  const fromGrouped = serializeProductAssetSlotArray(
    getGroupedAssetArrayForFormField(
      assets,
      formFieldKey,
      prepareAddProductFormValues(formFieldKey)
    )
  )
  const fromRows = buildAssetSlotsFromProductAssetRows(
    product,
    formFieldKey,
    slotCount
  )

  if (!fromGrouped.some(slot => Boolean(slot.id))) {
    return normalizeProductFormAssetSlots(
      fromRows.some(slot => Boolean(slot.id)) ? fromRows : fromGrouped,
      slotCount
    )
  }

  const merged = Array.from({ length: slotCount }, (_, index) => ({
    id: fromGrouped[index]?.id || fromRows[index]?.id || ''
  }))

  return normalizeProductFormAssetSlots(merged, slotCount)
}

/** `sliderPhotoSlots.Slider` — UUID strings in carousel order (GET product source of truth). */
export function getSliderPhotoSlotUuids(
  sliderPhotoSlots: Record<string, unknown> | undefined | null
): string[] {
  if (!sliderPhotoSlots || typeof sliderPhotoSlots !== 'object') {
    return []
  }

  const raw =
    sliderPhotoSlots[SLIDER_API_FIELD_NAME] ??
    sliderPhotoSlots[SLIDER_FORM_FIELD_KEY] ??
    sliderPhotoSlots.Slider

  if (!Array.isArray(raw)) {
    return []
  }

  return raw.map(entry =>
    entry == null ? '' : String(entry).trim()
  )
}

/** Read slider slots for POST/PATCH — prefer filled ids from RHF, then submit payload. */
export function getSliderSlotsFromForm(
  getValues: (name: string) => unknown,
  submitValues?: ProductFormValues['slider']
): IIdString[] {
  const fromForm = serializeProductAssetSlotArray(
    getValues(SLIDER_FORM_FIELD_KEY)
  )

  if (fromForm.some(slot => Boolean(slot.id))) {
    return fromForm
  }

  const fromSubmit = serializeProductAssetSlotArray(submitValues)

  if (fromSubmit.some(slot => Boolean(slot.id))) {
    return fromSubmit
  }

  return fromForm.length > 0 ? fromForm : fromSubmit
}

/** Pad or trim asset slots to the resolved slot count (category cap when set). */
export function normalizeProductFormAssetSlots(
  slots: IIdString[] | undefined,
  slotCount: number
): IIdString[] {
  const mapped = serializeProductAssetSlotArray(slots)

  while (mapped.length < slotCount) {
    mapped.push({ ...OBJECT_WITH_ID })
  }

  return mapped.slice(0, slotCount)
}

/**
 * Slider slots from `assets.Slider`, then `sliderPhotoSlots.Slider` (UUID list).
 * Slider and Photos are separate; Haier UI may pick Photo-type assets for Slider slots only.
 */
export function getSliderSlotsFromProduct(
  product: Pick<IEditProduct, 'assets' | 'sliderPhotoSlots'>
): IIdString[] {
  const fromAssets = getSliderSlotsFromProductAssets(
    product.assets
  )

  if (fromAssets.some(slot => Boolean(slot.id))) {
    return fromAssets
  }

  const uuids = getSliderPhotoSlotUuids(product.sliderPhotoSlots ?? undefined)

  if (uuids.some(id => Boolean(id))) {
    return serializeProductAssetSlotArray(uuids)
  }

  return fromAssets
}

/** Read `assets.dimensionsAccesories` from GET product (API key). */
export function getDimensionsAccessoriesSlotsFromProductAssets(
  assets: Record<string, unknown> | undefined | null
): IIdString[] {
  if (!assets) {
    return []
  }

  const raw =
    assets[DIMENSIONS_ACCESSORIES_API_FIELD_NAME] ??
    assets.dimensionsAccesories

  return serializeProductAssetSlotArray(raw)
}

/** Read dimensions accessory slots from product root or hydrated assets. */
export function getDimensionsAccessoriesSlotsFromProduct(
  product: Pick<
    IEditProduct,
    'assets' | 'dimensionsAccessorySlots'
  >
): IIdString[] {
  const fromAssets = getDimensionsAccessoriesSlotsFromProductAssets(
    product.assets
  )

  if (fromAssets.some(slot => Boolean(slot.id))) {
    return fromAssets
  }

  const uuids = product.dimensionsAccessorySlots?.DimensionsAccessories

  if (Array.isArray(uuids) && uuids.length > 0) {
    return uuids.map(id => ({ id: id ?? '' }))
  }

  return fromAssets
}

export function getDimensionsAccessoriesSlotsFromForm(
  getValues: (
    name: typeof DIMENSIONS_ACCESSORIES_FORM_FIELD_KEY
  ) => ProductFormValues['dimensionsAccesories']
): IIdString[] {
  return serializeProductAssetSlotArray(
    getValues(DIMENSIONS_ACCESSORIES_FORM_FIELD_KEY)
  )
}

export function createDefaultDimensionsAccessoriesSlots(): IIdString[] {
  return Array(DIMENSIONS_ACCESSORIES_DEFAULT_SLOT_COUNT).fill(OBJECT_WITH_ID)
}

/** Compare asset slot lists by `{ id }` only (order-sensitive). */
export function haveSameAssetSlotIds(a: unknown, b: unknown): boolean {
  const slotsA = serializeProductAssetSlotArray(a)
  const slotsB = serializeProductAssetSlotArray(b)

  if (slotsA.length !== slotsB.length) {
    return false
  }

  return slotsA.every((slot, index) => slot.id === slotsB[index]?.id)
}

export class ProductFormService {
  addProductFormDataPayloadFields: AddProductFormFieldNames = {
    sku: 'sku',
    skuCodes: 'skuCodes',
    name: 'name',
    shortName: 'shortName',
    title: 'title',
    subTitle: 'subTitle',
    packshotUrl: 'packshotUrl',
    packshotAlt: 'packshotAlt',
    packshotAiUsage: 'packshotAiUsage',
    packshotBackgroundDisabled: 'packshotBackgroundDisabled',
    openedPackshotUrl: 'openedPackshotUrl',
    openPackshotAlt: 'openedPackshotAlt',
    openedPackshotAiUsage: 'openedPackshotAiUsage',
    openedPackshot: 'openedPackshot',
    packshot: 'packshot',
    packshotPreviewUrl: 'packshotPreviewUrl',
    productCategoryId: 'productCategoryId',
    country: 'country',
    banner: 'banner',
    first_lead: 'first_lead',
    attributes: 'attributes',
    icons: 'icons',
    features: 'features',
    intro: 'intro',
    hotpoints: 'hotpoints',
    hotpointsAlt: 'hotpointsAlt',
    hotpointsAiUsage: 'hotpointsAiUsage',
    hotpointsMobile: 'hotpointsMobile',
    hotpointsMobileAlt: 'hotpointsMobileAlt',
    hotpointsMobileAiUsage: 'hotpointsMobileAiUsage',
    hotpointsProperties: 'hotpointsProperties',
    hotpointsMobileProperties: 'hotpointsMobileProperties',
    second_lead: 'second_lead',
    mainVideoTitle: 'mainVideoTitle',
    mainVideoYoutubeId: 'mainVideoYoutubeId',
    mainVideo: 'mainVideo',
    mainVideoDescriptions: 'mainVideoDescriptions',
    functions: 'functions',
    dimensionsAlt: 'dimensionsAlt',
    dimensionsDescription: 'dimensionsDescription',
    dimensions: 'dimensions',
    dimensionsUrl: 'dimensionsUrl',
    dimensionsTitle: 'dimensionsTitle',
    dimensionsTechnicalDrawing: 'dimensionsTechnicalDrawing',
    dimensionsTechnicalDrawingUrl: 'dimensionsTechnicalDrawingUrl',
    dimensionsTechnicalDrawingAlt: 'dimensionsTechnicalDrawingAlt',
    dimensionsAccesories: 'dimensionsAccesories',
    summary: 'summary',
    summaryUrl: 'summaryUrl',
    summaryAiUsage: 'summaryAiUsage',
    summaryProperties: 'summaryProperties',
    summaryDescription: 'summaryDescription',
    summaryAlt: 'summaryAlt',
    videoFunctions: 'videoFunctions',
    icon_functions: 'icon_functions',
    comparison: 'comparison',
    comparisonCustom: 'comparisonCustom',
    photos: 'photos',
    slider: 'slider',
    accessories: 'accessories',
    bottomVideoYoutubeId: 'bottomVideoYoutubeId',
    bottomVideo: 'bottomVideo',
    internalNote: 'internalNote',
    cardTemplate: 'cardTemplate',
    hotpointTemplate: 'hotpointTemplate'
  }

  addProductSchema = object().shape({
    sku: string().required('SKU of the product is required'),
    name: string().required('Name of the product is required'),
    shortName: string().required('Short name of the product is required'),
    title: string().nullable(),
    subTitle: string().nullable(),
    internalNote: string().nullable(),
    cardTemplate: string().nullable(),
    hotpointTemplate: string().nullable(),
    packshot: mixed().nullable(),
    packshotAlt: string().nullable(),
    openedPackshot: mixed().nullable(),
    // banner: string().required('Banner file is required'),
    openedPackshotAlt: string().nullable(),
    banner: array().of(
      object().shape({
        id: string()
      })
    ),
    // first_lead: string(),
    first_lead: array().of(
      object().shape({
        id: string()
      })
    ),
    attributes: array().of(
      object().shape({
        id: string()
      })
    ),
    icons: array().of(
      object().shape({
        id: string()
      })
    ),
    features: array().of(
      object().shape({
        id: string()
      })
    ),
    intro: array().of(
      object().shape({
        id: string()
      })
    ),
    hotpoints: mixed().nullable(),
    hotpointsAlt: string().nullable(),
    hotpointsMobile: mixed().nullable(),
    hotpointsMobileAlt: string().nullable(),
    hotpointsProperties: array()
      .of(
        object().shape({
          id: string(),
          content: object().shape({
            title: string(),
            description: string(),
            position: boolean()
          }),
          coords: object().shape({
            x: number(),
            y: number()
          })
        })
      )
      .nullable(),
      hotpointsMobileProperties: array()
      .of(
        object().shape({
          id: string(),
          content: object().shape({
            title: string(),
            description: string(),
            position: boolean()
          }),
          coords: object().shape({
            x: number(),
            y: number()
          })
        })
      )
      .nullable(),
    second_lead: array().of(
      object().shape({
        id: string()
      })
    ),
    mainVideoTitle: string(),
    mainVideoYoutubeId: string(),
    mainVideo: mixed()
      .test(
        'fileSize',
        'The video file size must be less than or equal to 600MB',
        (value: any) => {
          if (!value) return true // If no file is provided, validation is ok

          return isVideoSizeAllowed(value[0])
        }
      )
      .test('fileType', 'The video file type is not allowed', (value: any) => {
        if (!value) return true // If no file is provided, validation is ok

        return isVideoTypeAllowed(value[0])
      }),
    mainVideoDescriptions: array().of(
      object().shape({
        partnerName: string(),
        description: string()
      })
    ),
    functions: array().of(
      object().shape({
        id: string()
      })
    ),
    dimensionsDescription: string().nullable(),
    dimensions: mixed().nullable(),
    dimensionsAlt: string().nullable(),
    dimensionsTitle: string().nullable(),
    dimensionsTechnicalDrawing: mixed().nullable(),
    dimensionsTechnicalDrawingAlt: string().nullable(),
    dimensionsAccesories: array().of(
      object().shape({
        id: string()
      })
    ),
    summary: mixed().nullable(),
    summaryProperties: array().of(
      object().shape({
        name: string(),
        value: string()
      })
    ),
    summaryDescription: string().nullable(),
    summaryAlt: string().nullable(),
    videoFunctions: array().of(
      object().shape({
        youtubeId: string(),
        descriptions: array().of(
          object().shape({
            partnerName: string(),
            description: string()
          })
        )
      })
    ),
    icon_functions: array().of(
      object().shape({
        id: string()
      })
    ),
    comparison: object()
      .nullable()
      .shape({
        paramNames: array().of(string()),
        comparedProducts: array().of(
          object().shape({
            product: mixed(),
            paramValues: array().of(string())
          })
        )
      }),
    photos: array().of(
      object().shape({
        id: string()
      })
    ),
    slider: array().of(
      object().shape({
        id: string()
      })
    ),
    accessories: array().of(
      object().shape({
        id: string()
      })
    ),
    bottomVideoYoutubeId: string(),
    bottomVideo: mixed()
      .nullable()
      .test(
        'fileSize',
        'The video file size must be less than or equal to 600MB',
        (value: any) => {
          if (!value) return true // If no file is provided, validation is ok

          return isVideoSizeAllowed(value[0])
        }
      )
      .test('fileType', 'The video file type is not allowed', (value: any) => {
        if (!value) return true // If no file is provided, validation is ok

        return isVideoTypeAllowed(value[0])
      }),
    isDummyPackshot: boolean().optional(),
    packshotBackgroundDisabled: boolean().optional()
  })

  editProductSchema = object().shape({
    sku: string().required('SKU of the product is required'),
    name: string().required('Name of the product is required'),
    shortName: string().required('Short name of the product is required'),
    title: string().nullable(),
    subTitle: string().nullable(),
    internalNote: string().nullable(),
    cardTemplate: string().nullable(),
    hotpointTemplate: string().nullable(),
    packshot: mixed().nullable(),
    packshotUrl: string().nullable(),
    packshotAlt: string().nullable(),
    openedPackshot: mixed().nullable(),
    openedPackshotUrl: string().nullable(),
    openedPackshotAlt: string().nullable(),
    banner: array().of(
      object().shape({
        id: string()
      })
    ),
    first_lead: array().of(
      object().shape({
        id: string()
      })
    ),
    attributes: array().of(
      object().shape({
        id: string()
      })
    ),
    icons: array().of(
      object().shape({
        id: string()
      })
    ),
    features: array().of(
      object().shape({
        id: string()
      })
    ),
    intro: array().of(
      object().shape({
        id: string()
      })
    ),
    hotpoints: mixed().nullable(),
    hotpointsMobile: mixed().nullable(),
    hotpointsUrl: string().nullable(),
    hotpointsAlt: string().nullable(),
    hotpointsMobileUrl: string().nullable(),
    hotpointsMobileAlt: string().nullable(),
    hotpointsProperties: array()
      .of(
        object().shape({
          id: string(),
          content: object().shape({
            title: string(),
            description: string(),
            position: string()
          }),
          coords: object().shape({
            x: number(),
            y: number()
          })
        })
      )
      .nullable(),
      hotpointsMobileProperties: array()
      .of(
        object().shape({
          id: string(),
          content: object().shape({
            title: string(),
            description: string(),
            position: string()
          }),
          coords: object().shape({
            x: number(),
            y: number()
          })
        })
      )
      .nullable(),
    second_lead: array().of(
      object().shape({
        id: string()
      })
    ),
    mainVideoTitle: string(),
    mainVideoYoutubeId: string(),
    mainVideo: mixed()
      .test(
        'fileSize',
        'The video file size must be less than or equal to 600MB',
        (value: any) => {
          if (!value) return true // If no file is provided, validation is ok

          return isVideoSizeAllowed(value[0])
        }
      )
      .test('fileType', 'The video file type is not allowed', (value: any) => {
        if (!value) return true // If no file is provided, validation is ok

        return isVideoTypeAllowed(value[0])
      }),
    mainVideoDescriptions: array().of(
      object().shape({
        partnerName: string(),
        description: string()
      })
    ),
    functions: array().of(
      object().shape({
        id: string()
      })
    ),
    dimensionsDescription: string().nullable(),
    dimensions: mixed().nullable(),
    dimensionsUrl: string().nullable(),
    dimensionsAlt: string().nullable(),
    dimensionsTitle: string().nullable(),
    dimensionsTechnicalDrawing: mixed().nullable(),
    dimensionsTechnicalDrawingUrl: string().nullable(),
    dimensionsTechnicalDrawingAlt: string().nullable(),
    dimensionsAccesories: array().of(
      object().shape({
        id: string()
      })
    ),
    summary: mixed().nullable(),
    summaryUrl: mixed().nullable(),
    summaryAlt: string().nullable(),
    summaryProperties: array().of(
      object().shape({
        name: string(),
        value: string()
      })
    ),
    summaryDescription: string().nullable(),
    videoFunctions: array().of(
      object().shape({
        youtubeId: string(),
        descriptions: array().of(
          object().shape({
            partnerName: string(),
            description: string()
          })
        )
      })
    ),
    icon_functions: array().of(
      object().shape({
        id: string()
      })
    ),
    comparison: object()
      .nullable()
      .shape({
        paramNames: array().of(string()),
        comparedProducts: array().of(
          object().shape({
            product: mixed(),
            paramValues: array().of(string())
          })
        )
      }),
    photos: array().of(
      object().shape({
        id: string()
      })
    ),
    slider: array().of(
      object().shape({
        id: string()
      })
    ),
    accessories: array().of(
      object().shape({
        id: string()
      })
    ),
    bottomVideoYoutubeId: string(),
    bottomVideo: mixed()
      .nullable()
      .test(
        'fileSize',
        'The video file size must be less than or equal to 600MB',
        (value: any) => {
          if (!value) return true // If no file is provided, validation is ok

          return isVideoSizeAllowed(value[0])
        }
      )
      .test('fileType', 'The video file type is not allowed', (value: any) => {
        if (!value) return true // If no file is provided, validation is ok

        return isVideoTypeAllowed(value[0])
      }),
    isDummyPackshot: boolean().optional(),
    packshotBackgroundDisabled: boolean().optional()
  })

  mapAddProductFormValuesToPayload(
    values: ProductFormValues,
    brand?: string,
    options?: MapProductFormPayloadOptions
  ): FormData {
    const formData = new FormData()

    const comparisonCustomRowFiles = values.comparisonCustomRowImageFiles
    /** Remapped to stripped `comparisonCustom.rows` indices (Schneider image slots). */
    let schneiderComparisonCustomImageFiles:
      | (File | null)[]
      | undefined

    // get field names that represent files
    const fileFields = [
      'packshot',
      'openedPackshot',
      'hotpoints',
      'hotpointsMobile',
      'mainVideo',
      'dimensions',
      'dimensionsTechnicalDrawing',
      'summary',
      'bottomVideo'
    ]

    // @agata - remove fields that should not be sent to API (packshotPreviewUrl should be always removed, comparison - until API supports it)
    const fieldsToRemove = [
      'packshotPreviewUrl',
      'comparisonCustomRowImageFiles',
      'dimensionsTechnicalDrawingUrl'
    ] as const
    let valuesToSend = omit(fieldsToRemove, values) as ProductFormValues

    if (!brand || !isSchneider(brand)) {
      valuesToSend = omit(
        ['comparisonCustom'],
        valuesToSend
      )
    } else {
      const ccRaw = valuesToSend.comparisonCustom

      if (ccRaw === undefined || ccRaw === null) {
        valuesToSend = omit(
          ['comparisonCustom'],
          valuesToSend
        )
      } else {
        const afterCols = stripTrailingEmptyComparisonCustomColumns(ccRaw)
        const { cc: ccStripped, keptOriginalRowIndices } =
          stripEmptyComparisonCustomRows(afterCols, comparisonCustomRowFiles)

        if (isComparisonCustomEmpty(ccStripped)) {
          valuesToSend = omit(
            ['comparisonCustom'],
            valuesToSend
          )
        } else {
          const remappedRowFiles = keptOriginalRowIndices.map(
            i => comparisonCustomRowFiles?.[i] ?? null
          )

          schneiderComparisonCustomImageFiles = remappedRowFiles
          valuesToSend = {
            ...valuesToSend,
            comparisonCustom: stripComparisonCustomRowImagesForUpload(
              ccStripped,
              remappedRowFiles
            )
          }
        }
      }
    }

    // internalNote: only Haier / Hoover / Candy (incl. HaierTV); always send string ("" clears note on backend)
    if (!brand || !isHaierHooverCandy(brand)) {
      valuesToSend = omit(['internalNote'], valuesToSend)
    } else {
      const note = valuesToSend.internalNote

      valuesToSend.internalNote =
        note === undefined || note === null ? '' : String(note).trim()
    }

    if (!valuesToSend.cardTemplate) {
      valuesToSend = omit(['cardTemplate'], valuesToSend)
    }

    if (!valuesToSend.hotpointTemplate) {
      valuesToSend = omit(
        ['hotpointTemplate'],
        valuesToSend
      )
    }

    const imageAiUsageFields = [
      'packshotAiUsage',
      'openedPackshotAiUsage',
      'hotpointsAiUsage',
      'hotpointsMobileAiUsage',
      'summaryAiUsage'
    ] as const

    for (const field of imageAiUsageFields) {
      if (
        options?.isUpdate &&
        options.dirtyFields &&
        !options.dirtyFields[field]
      ) {
        continue
      }

      const v = valuesToSend[field]

      if (v === undefined) continue
      formData.append(field, v === null ? 'null' : v)
    }
    valuesToSend = omit(
      [...imageAiUsageFields],
      valuesToSend
    )

    const assetSlotFormKeys = new Set<string>([
      ...ORDER_OF_ASSETS,
      DIMENSIONS_ACCESSORIES_FORM_FIELD_KEY
    ])

    /**
     * Append values from form to FormData
     */
    for (const key in valuesToSend) {
      const raw = valuesToSend[key as keyof ProductFormValues]

      // RHF leaves optional fields as `undefined`; FormData would send the string "undefined".
      if (raw === undefined || raw === null) {
        continue
      }

      if (typeof raw === 'object') {
        if (fileFields.includes(key)) {
          const fileList = raw as FileList
          const files = fileList?.[0]

          if (files) {
            formData.append(prepareAddProductFormValues(key), files)
          }
          continue
        }

        const serialized = assetSlotFormKeys.has(key)
          ? serializeProductAssetSlotArray(raw)
          : raw

        formData.append(
          prepareAddProductFormValues(key),
          JSON.stringify(serialized)
        )
        continue
      }

      let value = String(raw)

      if (value === 'undefined' || value === 'null') {
        continue
      }

      if (key === 'skuCodes' && value) {
        value = value.trim()
      }
      formData.append(prepareAddProductFormValues(key), value)
    }

    if (
      brand &&
      isSchneider(brand) &&
      schneiderComparisonCustomImageFiles?.length
    ) {
      const slots: number[] = []
      const files: File[] = []

      schneiderComparisonCustomImageFiles.forEach((f, i) => {
        if (f) {
          files.push(f)
          slots.push(i)
        }
      })
      if (files.length) {
        formData.append('comparisonCustomImageSlots', JSON.stringify(slots))
        files.forEach(f => formData.append('comparisonCustomImages', f))
      }
    }

    /**
     * return form data for other modules to use
     */
    return formData
  }

  // @sieradz - communication with API passed out to [{id: ''}]
  // createValue = key => {
  //   // @sieradz - if the field is of type string then insert string
  //   if (STRINGS.includes(key)) {
  //     return STRING
  //   }

  //   // @sieradz - if the field is of type array then insert objects like: [{id: ''}]
  //   if (ARRAYS_OF_OBJECT_WITH_ID[key]) {
  //     return Array(ARRAYS_OF_OBJECT_WITH_ID[key]).fill(OBJECT_WITH_ID)
  //   }

  //   return null
  // }

  /**
 * Haier may show Slider using the Photos asset pool; saved ids go only to
 * `slider_photo_slots` via the `Slider` multipart field (not the Photos field).
 */
  ensureDimensionsAccessoriesFormSlots(
    values: ProductFormValues,
    initialEditedProduct?: IEditProduct
  ): ProductFormValues {
    const fromForm = values.dimensionsAccesories
    const fromProduct = initialEditedProduct
      ? getDimensionsAccessoriesSlotsFromProduct(initialEditedProduct)
      : undefined

    const formHasAssetIds = fromForm?.some(slot => Boolean(slot?.id))
    const productHasAssetIds = fromProduct?.some(slot => Boolean(slot?.id))

    let resolved: IIdString[]

    if (formHasAssetIds && fromForm) {
      resolved = fromForm
    } else if (productHasAssetIds && fromProduct) {
      resolved = fromProduct
    } else {
      resolved =
        fromProduct ??
        fromForm ??
        createDefaultDimensionsAccessoriesSlots()
    }

    const padded = resolved.map(slot => ({ id: slot.id ?? '' }))

    while (padded.length < DIMENSIONS_ACCESSORIES_DEFAULT_SLOT_COUNT) {
      padded.push({ ...OBJECT_WITH_ID })
    }

    return {
      ...values,
      [DIMENSIONS_ACCESSORIES_FORM_FIELD_KEY]: padded.slice(
        0,
        DIMENSIONS_ACCESSORIES_DEFAULT_SLOT_COUNT
      )
    }
  }

  ensureHaierSliderFormSlots(
    values: Record<string, IIdString[]>,
    brand?: string,
    initialAssets?: IInitialAssets
  ): Record<string, IIdString[]> {
    if (!isHaierBrand(brand)) {
      return values
    }

    const fromForm = values[SLIDER_FORM_FIELD_KEY]
    const fromProduct =
      initialAssets != null
        ? this.createInitialValue(SLIDER_FORM_FIELD_KEY, initialAssets)
        : undefined

    const formHasAssetIds = fromForm?.some(slot => Boolean(slot?.id))
    const productHasAssetIds = fromProduct?.some(slot => Boolean(slot?.id))

    if (formHasAssetIds && fromForm) {
      return { ...values, [SLIDER_FORM_FIELD_KEY]: fromForm }
    }

    if (productHasAssetIds && fromProduct) {
      return { ...values, [SLIDER_FORM_FIELD_KEY]: fromProduct }
    }

    return {
      ...values,
      [SLIDER_FORM_FIELD_KEY]:
        fromProduct ?? fromForm ?? this.createValue(SLIDER_FORM_FIELD_KEY)
    }
  }

  /** Resolve slots from form key (`slider`) or API/DB key (`Slider`). */
  getInitialAssetArray(
    initialAssets: IInitialAssets,
    formKey: string
  ): { id: string }[] | undefined {
    const direct = initialAssets[formKey]

    if (Array.isArray(direct) && direct.length > 0) {
      return direct
    }

    const apiKey = prepareAddProductFormValues(formKey)

    if (apiKey === formKey) {
      return undefined
    }

    const fromApi = initialAssets[apiKey]

    if (Array.isArray(fromApi) && fromApi.length > 0) {
      return fromApi
    }

    return undefined
  }

  getAssetSlotCount(
    key: string,
    maxNumberPerProduct?: number,
    cardTemplate?: string | null
  ): number {
    const fallback =
      ARRAYS_OF_OBJECT_WITH_ID[key as keyof typeof ARRAYS_OF_OBJECT_WITH_ID] ??
      1
    const fromCategory =
      typeof maxNumberPerProduct === 'number' && maxNumberPerProduct > 0
        ? maxNumberPerProduct
        : undefined

    // Category `maxNumberPerProduct` is authoritative when configured in admin.
    if (fromCategory !== undefined) {
      return fromCategory
    }

    const fromTemplate = getMinAssetSlotCountForCardTemplate(key, cardTemplate)

    if (fromTemplate !== undefined) {
      return Math.max(fallback, fromTemplate)
    }

    return fallback
  }

  slotCountForAssetType(
    key: string,
    maxNumberPerProduct?: number,
    cardTemplate?: string | null
  ): number {
    return this.getAssetSlotCount(key, maxNumberPerProduct, cardTemplate)
  }

  createValue = (
    key: string,
    maxNumberPerProduct?: number,
    cardTemplate?: string | null
  ) =>
    Array(this.slotCountForAssetType(key, maxNumberPerProduct, cardTemplate)).fill(
      OBJECT_WITH_ID
    )

  createInitialValue = (
    key: string,
    initialAssets: IInitialAssets,
    maxNumberPerProduct?: number,
    cardTemplate?: string | null,
    initialEditedProduct?: IEditProduct
  ) => {
    const slotCount = this.slotCountForAssetType(
      key,
      maxNumberPerProduct,
      cardTemplate
    )

    if (initialEditedProduct) {
      return resolveProductFormAssetSlots(
        initialEditedProduct as unknown as Record<string, unknown>,
        key,
        slotCount
      )
    }

    const existing = this.getInitialAssetArray(initialAssets, key)

    if (existing) {
      return normalizeProductFormAssetSlots(
        serializeProductAssetSlotArray(existing),
        slotCount
      )
    }

    return Array(slotCount).fill(OBJECT_WITH_ID)
  }

  getAddProductDefaultValues({
    country,
    productCategoryId,
    assets,
    brand
  }: {
    country: string
    productCategoryId: string
    assets: IAssets
    brand: string
  }): ProductFormValues {
    const coreProductFormValues = {
      packshot: null,
      // @sieradz - no initialization state to avoid sending unnecessarily undefined in payload (during create product) - the API does not support this, it only accepts FileList. When editing this field react hook form automatically updates the field
      // openedPackshot: null,
      // @sieradz - no initialization state to avoid sending unnecessarily undefined in payload (during create product) - the API does not support this, it only accepts FileList. When editing this field react hook form automatically updates the field
      // bottomVideo: null,
      // @sieradz - no initialization state to avoid sending unnecessarily undefined in payload (during create product) - the API does not support this, it only accepts FileList. When editing this field react hook form automatically updates the field
      // dimensions: null,
      // @sieradz - no initialization state to avoid sending unnecessarily undefined in payload (during create product) - the API does not support this, it only accepts FileList. When editing this field react hook form automatically updates the field
      // summary: null,
      packshotAlt: '',
      packshotBackgroundDisabled: false,
      sku: '',
      skuCodes: '',
      name: '',
      shortName: '',
      internalNote: '',
      country,
      productCategoryId,
      banner: [{ id: '' }],
      mainVideoTitle: '',
      mainVideoYoutubeId: '',
      dimensionsDescription: '',
      dimensionsTitle: '',
      dimensionsTechnicalDrawingAlt: '',
      dimensionsAccesories: createDefaultDimensionsAccessoriesSlots(),
      summaryProperties: [{ name: '', value: '' }],
      summaryDescription: '',
      summaryAlt: '',
      mainVideoDescriptions: [
        {
          partnerName: VIDEO_DESCRIPTIONS_TYPES.DEFAULT,
          description: ''
        },
        {
          partnerName: VIDEO_DESCRIPTIONS_TYPES.euro,
          description: ''
        },
        {
          partnerName: VIDEO_DESCRIPTIONS_TYPES.mediaExpert,
          description: ''
        }
      ],
      videoFunctions: [initialVideoFunction],
      comparison: {
        paramNames: [''],
        comparedProducts: [
          {
            product: initialComparedProduct,
            paramValues: ['']
          },
          {
            product: initialComparedProduct,
            paramValues: ['']
          },
          {
            product: initialComparedProduct,
            paramValues: ['']
          },
          {
            product: initialComparedProduct,
            paramValues: ['']
          },
          {
            product: initialComparedProduct,
            paramValues: ['']
          }
        ]
      }
    }

    const addProductFormValues = this.ensureHaierSliderFormSlots(
      Object.entries(assets).reduce(
        (values, [key, assetType]) => {
          // @ts-ignore
          values[key] = this.createValue(key, assetType?.maxNumberPerProduct)

          return values
        },
        {} as Record<string, IIdString[]>
      ),
      brand
    )

    return {
      ...coreProductFormValues,
      ...addProductFormValues,
      ...(isSchneider(brand)
        ? {
            comparison: emptyComparison,
            comparisonCustom: getDefaultComparisonCustom(),
            comparisonCustomRowImageFiles: []
          }
        : {})
    }
  }

  getEditProductInitialValues({
    country,
    productCategoryId,
    assets,
    initialEditedProduct
  }: {
    country: string
    productCategoryId: string
    assets: IAssets
    initialEditedProduct: IEditProduct | undefined
  }): ProductFormValues {
    const editBrand = initialEditedProduct?.productCategory?.brand?.name ?? ''
    const resolvedCardTemplate = resolveCardTemplate(
      initialEditedProduct?.cardTemplate,
      editBrand,
      {
        categoryName: initialEditedProduct?.productCategory?.name,
        productName: initialEditedProduct?.name,
        country
      }
    )

    const mainVideoDescriptions = initialEditedProduct?.mainVideoDescriptions
      ? (JSON.parse(
          initialEditedProduct.mainVideoDescriptions
        ) as IDescriptions[])
      : // @sieradz default IDescriptions[]
        [
          {
            partnerName: '',
            description: ''
          }
        ]

    const videoFunctions = initialEditedProduct?.videoFunctions
      ? (JSON.parse(initialEditedProduct.videoFunctions) as IVideoFunctions[])
      : // @sieradz default IVideoFunctions[]
        [
          {
            youtubeId: '',
            descriptions: [
              {
                partnerName: '',
                description: ''
              }
            ]
          }
        ]

    const summaryProperties = initialEditedProduct?.summaryProperties
      ? (JSON.parse(
          initialEditedProduct.summaryProperties
        ) as ISummaryProperties[])
      : // @sieradz default ISummaryProperties[]
        [{ name: '', value: '' }]

    const hotpointsProperties = initialEditedProduct?.hotpointsProperties
      ? (
          JSON.parse(initialEditedProduct.hotpointsProperties) as IHotpoint[]
        ).map(item => {
          return {
            ...item,
            content: {
              ...item.content,
              position: item.content.position === 'right' ? true : false
            }
          }
        })
      : undefined

      const hotpointsMobileProperties = initialEditedProduct?.hotpointsMobileProperties
      ? (
          JSON.parse(initialEditedProduct.hotpointsMobileProperties) as IHotpoint[]
        ).map(item => {
          return {
            ...item,
            content: {
              ...item.content,
              position: item.content.position === 'right' ? true : false
            }
          }
        })
      : undefined

    const initialAssets = Object.fromEntries(
      Object.entries(initialEditedProduct?.assets ?? {}).map(([k, v]) => [
        assetTypeNameToFormKey(k),
        v
      ])
    ) as IInitialAssets

    const sliderFromProduct = initialEditedProduct
      ? getSliderSlotsFromProduct(initialEditedProduct)
      : []

    if (
      isHaierBrand(editBrand) ||
      sliderFromProduct.some(slot => Boolean(slot.id))
    ) {
      initialAssets[SLIDER_FORM_FIELD_KEY] = sliderFromProduct
    }

    const rawComparisonCustom = initialEditedProduct?.comparisonCustom
    let initialComparisonCustom: IComparisonCustom | null = null

    if (rawComparisonCustom != null) {
      initialComparisonCustom =
        typeof rawComparisonCustom === 'string'
          ? (JSON.parse(rawComparisonCustom) as IComparisonCustom)
          : rawComparisonCustom
    }

    // @agata - comparison initial values - in response, in comparison.comparedProducts there is always one item
    // which has null in product field - this is current product
    const notCurrentProductsToCompare = initialEditedProduct?.comparison
      ?.comparedProducts?.length
      ? initialEditedProduct?.comparison?.comparedProducts?.filter(
          item => item?.product !== null
        )
      : []

    const initialComparison: IComparison = isSchneider(editBrand)
      ? emptyComparison
      : {
          paramNames: initialEditedProduct?.comparison?.paramNames || [''],
          comparedProducts: [
            {
              product: {
                id: initialEditedProduct?.id || '',
                shortName: initialEditedProduct?.shortName || '',
                sku: initialEditedProduct?.sku || '',
                skuCodes: initialEditedProduct?.skuCodes || '',
                name: initialEditedProduct?.name || '',
                packshotUrl: initialEditedProduct?.packshotUrl || '',
                isDummyPackshot: initialEditedProduct?.isDummyPackshot || false
              },
              paramValues:
                initialEditedProduct?.comparison?.comparedProducts.find(
                  item => item?.product === null
                )?.paramValues ||
                Array(
                  initialEditedProduct?.comparison?.paramNames?.length
                ).fill('')
            },
            {
              product:
                notCurrentProductsToCompare[0]?.product ||
                initialComparedProduct,
              paramValues:
                notCurrentProductsToCompare[0]?.paramValues ||
                Array(
                  initialEditedProduct?.comparison?.paramNames?.length
                ).fill('')
            },
            {
              product:
                notCurrentProductsToCompare[1]?.product ||
                initialComparedProduct,
              paramValues:
                notCurrentProductsToCompare[1]?.paramValues ||
                Array(
                  initialEditedProduct?.comparison?.paramNames?.length
                ).fill('')
            },
            {
              product:
                notCurrentProductsToCompare[2]?.product ||
                initialComparedProduct,
              paramValues:
                notCurrentProductsToCompare[2]?.paramValues ||
                Array(
                  initialEditedProduct?.comparison?.paramNames?.length
                ).fill('')
            },
            {
              product:
                notCurrentProductsToCompare[3]?.product ||
                initialComparedProduct,
              paramValues:
                notCurrentProductsToCompare[3]?.paramValues ||
                Array(
                  initialEditedProduct?.comparison?.paramNames?.length
                ).fill('')
            }
          ]
        }

    const coreProductFormValues = {
      packshot: null,
      packshotUrl: initialEditedProduct?.packshotUrl || '',
      packshotAlt: initialEditedProduct?.packshotAlt || '',
      packshotAiUsage: normalizeImageAiUsageForForm(
        initialEditedProduct?.packshotAiUsage
      ),
      openedPackshot: null,
      openedPackshotUrl: initialEditedProduct?.openedPackshotUrl || '',
      openedPackshotAlt: initialEditedProduct?.openedPackshotAlt || '',
      openedPackshotAiUsage: normalizeImageAiUsageForForm(
        initialEditedProduct?.openedPackshotAiUsage
      ),
      dimensions: null,
      dimensionsUrl: initialEditedProduct?.dimensionsUrl || '',
      dimensionsTitle: normalizeOptionalProductText(
        initialEditedProduct?.dimensionsTitle
      ),
      dimensionsTechnicalDrawing: null,
      dimensionsTechnicalDrawingUrl:
        initialEditedProduct?.dimensionsTechnicalDrawingUrl || '',
      dimensionsTechnicalDrawingAlt:
        initialEditedProduct?.dimensionsTechnicalDrawingAlt || '',
      summary: null,
      summaryUrl: initialEditedProduct?.summaryUrl || '',
      summaryAiUsage: normalizeImageAiUsageForForm(
        initialEditedProduct?.summaryAiUsage
      ),
      sku: initialEditedProduct?.sku || '',
      skuCodes: initialEditedProduct?.skuCodes || '',
      name: initialEditedProduct?.name || '',
      shortName: initialEditedProduct?.shortName || '',
      title: initialEditedProduct?.title || '',
      subTitle: initialEditedProduct?.subTitle,
      internalNote: initialEditedProduct?.internalNote ?? '',
      country,
      productCategoryId,
      banner: [{ id: '' }], // @sieradz - in addition, banner are filled / overwritten in addProductFormValues. So here is just in case ;)
      mainVideoTitle: initialEditedProduct?.mainVideoTitle || '',
      mainVideoYoutubeId: initialEditedProduct?.mainVideoYoutubeId || '',
      dimensionsDescription: normalizeOptionalProductText(
        initialEditedProduct?.dimensionsDescription
      ),
      dimensionsAlt: initialEditedProduct?.dimensionsAlt || '',
      hotpoints: null,
      hotpointsUrl: initialEditedProduct?.hotpointsUrl || '',
      hotpointsAlt: initialEditedProduct?.hotpointsAlt || '',
      hotpointsAiUsage: normalizeImageAiUsageForForm(
        initialEditedProduct?.hotpointsAiUsage
      ),
      hotpointsMobile: null,
      hotpointsMobileUrl: initialEditedProduct?.hotpointsMobileUrl || '',
      hotpointsMobileAlt: initialEditedProduct?.hotpointsMobileAlt || '',
      hotpointsMobileAiUsage: normalizeImageAiUsageForForm(
        initialEditedProduct?.hotpointsMobileAiUsage
      ),
      hotpointsProperties: hotpointsProperties,
      hotpointsMobileProperties: hotpointsMobileProperties,
      summaryProperties: summaryProperties,
      summaryDescription: initialEditedProduct?.summaryDescription || '',
      summaryAlt: initialEditedProduct?.summaryAlt || '',
      mainVideoDescriptions: [
        {
          partnerName: VIDEO_DESCRIPTIONS_TYPES.DEFAULT,
          description:
            mainVideoDescriptions.find(
              el => el.partnerName === VIDEO_DESCRIPTIONS_TYPES.DEFAULT
            )?.description || ''
        },
        {
          partnerName: VIDEO_DESCRIPTIONS_TYPES.euro,
          description:
            mainVideoDescriptions.find(
              el => el.partnerName === VIDEO_DESCRIPTIONS_TYPES.euro
            )?.description || ''
        },
        {
          partnerName: VIDEO_DESCRIPTIONS_TYPES.mediaExpert,
          description:
            mainVideoDescriptions.find(
              el => el.partnerName === VIDEO_DESCRIPTIONS_TYPES.mediaExpert
            )?.description || ''
        }
      ],
      videoFunctions: videoFunctions,
      comparison: initialComparison,
      ...(isSchneider(editBrand)
        ? {
            comparisonCustom: normalizeComparisonCustom(
              initialComparisonCustom ?? getDefaultComparisonCustom()
            ),
            comparisonCustomRowImageFiles: []
          }
        : {}),
      isDummyPackshot: initialEditedProduct?.isDummyPackshot || false,
      packshotBackgroundDisabled:
        initialEditedProduct?.packshotBackgroundDisabled ?? false,
      cardTemplate: initialEditedProduct?.cardTemplate ?? null,
      hotpointTemplate: initialEditedProduct?.hotpointTemplate ?? null
    }

    const addProductFormValues = this.ensureHaierSliderFormSlots(
      Object.entries(assets).reduce(
        (values, [key, assetType]) => {
          // @ts-ignore
          values[key] = this.createInitialValue(
            key,
            initialAssets,
            assetType?.maxNumberPerProduct,
            resolvedCardTemplate,
            initialEditedProduct
          )

          return values
        },
        {} as Record<string, IIdString[]>
      ),
      editBrand,
      initialAssets
    )

    const bottomVideoYoutubeId = {
      bottomVideoYoutubeId: initialEditedProduct?.bottomVideoYoutubeId || ''
    }
    const bottomVideo = { bottomVideo: null }

    return this.ensureDimensionsAccessoriesFormSlots(
      {
        ...coreProductFormValues,
        ...addProductFormValues,
        ...bottomVideoYoutubeId,
        ...bottomVideo
      },
      initialEditedProduct
    )
  }
}

export const ProductFormServiceInstance = new ProductFormService()

/** Grow or shrink a form asset slot array to match category / template minimums. */
export function resizeProductFormAssetSlots(
  slots: IIdString[] | undefined,
  formFieldKey: string,
  maxNumberPerProduct?: number,
  cardTemplate?: string | null
): IIdString[] {
  const slotCount = ProductFormServiceInstance.getAssetSlotCount(
    formFieldKey,
    maxNumberPerProduct,
    cardTemplate
  )

  return normalizeProductFormAssetSlots(
    serializeProductAssetSlotArray(slots),
    slotCount
  )
}

